import { $, $$, isMobile } from '@utils';
import removeAccentMarkAndUppercase from '@utils/RemoveAccentMarkAndUppercase';

function init() {
  if (!$('[data-au-select]')) return;

  addEventListeners();
}

function showOptions(listbox, listboxOptions, selectArrow) {
  if (listboxOptions.length <= 0) return;
  listbox.classList.remove('au-visually-hidden');

  listboxOptions[0].focus();
  listboxOptions.forEach((listboxOption) => {
    if (listboxOption.getAttribute('disabled') === null) {
      listboxOption.setAttribute('tabindex', '0');
    }
    // Add tooltip if text is wider than listbox element
    listboxAddTooltip(listboxOption);
  });
  selectArrow.classList.add('au-float-input__arrow--active');
  listbox.closest('[data-au-select]').classList.add('au-select--active');
}

function hideOptions(listbox, listboxOptions, selectArrow) {
  if (listboxOptions.length <= 0) return;
  listbox.classList.add('au-visually-hidden');
  listboxOptions.forEach((listboxOption) => {
    listboxOption.setAttribute('tabindex', '-1');

    // Remove tooltips
    listboxRemoveTooltip(listboxOption);
  });
  selectArrow.classList.remove('au-float-input__arrow--active');
  listbox.closest('[data-au-select]').classList.remove('au-select--active');
}

function listboxSelection(select, listbox, selectArrow) {
  const listboxOptions = select.querySelectorAll('[data-au-listbox-option]');
  if (listboxOptions.length <= 0) return;

  const options = select.querySelectorAll('[data-au-option]');
  const selectMultiple = select.querySelector('select').hasAttribute('multiple');

  listboxOptions.forEach((listboxOption, index) => {
    // Avoid re-adding listeners
    if (listboxOption.dataset.listenerAttached === 'true') return;

    listboxOption.addEventListener('click', (e) => {
      e.stopPropagation();
      const listboxOptionId = listboxOption.id;
      const listboxOptionValue = listboxOption.querySelector('.au-select__listbox-text').textContent.trim();
      const optionSelected = $(`[data-au-option="${listboxOptionId}"]`);
      const listboxOptionIsActive = listboxOption.classList.contains('au-active');
      const selectText = select.querySelector('[data-au-select-text]');
      const currentSelectText = selectText.textContent.trim();

      //SELECT NORMAL
      if (!selectMultiple) {
        // Print listbox option selected value in span
        selectText.textContent = listboxOptionValue;

        // Set all select options as not selected
        options.forEach((option) => {
          option.removeAttribute('selected');
        });

        // Set select option selected
        optionSelected.setAttribute('selected', true);

        // Remove active style from all listbox options and set aria-selected to false
        listboxOptions.forEach((listboxOption) => {
          listboxOption.classList.remove('au-active');
          if (listboxOption.hasAttribute('aria-selected')) {
            listboxOption.setAttribute('aria-selected', false);
          }
        });

        // Add active style to listbox option selected and set aria-selected to true
        listboxOption.classList.add('au-active');
        listboxOption.setAttribute('aria-selected', true);

        // Hide listbox when listbox option is selected
        hideOptions(listbox, listboxOptions, selectArrow);
      } else {
        //SELECT MULTIPLE
        // Print listbox option selected value in span
        if (currentSelectText.includes(listboxOptionValue)) {
          // Add listbox option selected value if is not present
          const updatedText = currentSelectText
            .split(', ')
            .filter((item) => item !== listboxOptionValue)
            .join(', ');

          selectText.textContent = updatedText;
        } else {
          // Remove listbox option selected value if is already present
          const updatedText = currentSelectText ? `${currentSelectText}, ${listboxOptionValue}` : listboxOptionValue;
          selectText.textContent = updatedText;
        }

        // Set select option selected
        // Add active style to listbox option selected and set aria-selected to true
        if (listboxOptionIsActive) {
          optionSelected.removeAttribute('selected');
          listboxOption.classList.remove('au-active');
          listboxOption.setAttribute('aria-selected', false);
        } else {
          optionSelected.setAttribute('selected', true);
          listboxOption.classList.add('au-active');
          listboxOption.setAttribute('aria-selected', true);
        }
      }

      // Add selected listbox option to aria-activedescendant in listbox
      listbox.setAttribute('aria-activedescendant', listboxOptionId);

      // Add tooltip to select if selected text overflows
      // first, if it exists, we remove it
      if (select.querySelector('[data-au-select-tooltip]')) {
        select.querySelector('[data-au-select-tooltip]').remove();
      }

      // create tooltip and append it to select only if text overflows and if it's not a mobile device
      if (!isMobile.matches && selectText.scrollWidth > selectText.offsetWidth) {
        const selectTooltip = document.createElement('span');
        selectTooltip.setAttribute('aria-hidden', true);
        selectTooltip.setAttribute('data-au-select-tooltip', '');
        selectTooltip.classList.add('au-select__tooltip');
        selectTooltip.textContent = selectText.innerText;

        select.appendChild(selectTooltip);

        selectText.addEventListener('mousemove', (e) => {
          selectTooltip.style.left = `${e.clientX - selectTooltip.offsetWidth / 2}px`;
          selectTooltip.style.top = `${e.clientY - 56}px`;

          selectTooltip.style.visibility = 'visible';
          selectTooltip.style.opacity = 1;
        });

        selectText.addEventListener('mouseleave', () => {
          selectTooltip.style.visibility = 'hidden';
          selectTooltip.style.opacity = 0;
        });
      }

      // datalayer
      const selectLabel = removeAccentMarkAndUppercase(select.querySelector('label').innerText);
      window.dataLayer.push({
        event: 'navigation',
        content_type: 'clickModulo',
        item_id: `${selectLabel}_${removeAccentMarkAndUppercase(selectText.innerText.split(', ').join('_'))}`,
      });
    });

    // Handle Keyboard navigation
    listboxOption.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextListboxOption = listboxOptions[index + 1] || listboxOptions[0];
        nextListboxOption.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevListBoxOption = listboxOptions[index - 1] || listboxOptions[listboxOptions.length - 1];
        prevListBoxOption.focus();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        listboxOption.click();
      }
    });

    // Hide listbox after tabulation
    listboxOption.addEventListener('blur', () => {
      if (index + 1 === listboxOptions.length) {
        hideOptions(listbox, listboxOptions, selectArrow);
      }
    });

    // Set flag so listeners aren't added again
    listboxOption.dataset.listenerAttached = 'true';
  });
}

function displaySelect(select, listbox, selectArrow) {
  const isDisplayed = !listbox.classList.contains('au-visually-hidden');
  const listboxOptions = select.querySelectorAll('[data-au-listbox-option]');

  // Show/hide listbox
  if (isDisplayed) {
    hideOptions(listbox, listboxOptions, selectArrow);
  } else {
    showOptions(listbox, listboxOptions, selectArrow);
  }

  // Close the dropdown when clicking outside of it
  document.addEventListener('click', (e) => {
    if (!select.contains(e.target) && !listbox.contains(e.target)) {
      hideOptions(listbox, listboxOptions, selectArrow);
    }

    if (!e.target.closest('[data-au-form-element]') || listbox.contains(e.target)) {
      selectForceFocusAndBlur(select.querySelector('select'));
    }
  });

  // Close the dropdown when press Esc
  document.addEventListener('keydown', (e) => {
    if (!isDisplayed && e.key === 'Escape') {
      hideOptions(listbox, listboxOptions, selectArrow);
    }
  });
}

function listboxAddTooltip(listboxOption) {
  const listBoxText = listboxOption.querySelector('[data-au-select-listbox-text]');

  if (listBoxText.scrollWidth > listBoxText.offsetWidth) {
    const listBoxTooltip = document.createElement('span');
    listBoxTooltip.setAttribute('aria-hidden', true);
    listBoxTooltip.setAttribute('data-au-select-listbox-tooltip', '');
    listBoxTooltip.classList.add('au-select__listbox-tooltip');
    listBoxTooltip.textContent = listBoxText.innerText;

    listboxOption.closest('[data-au-select]').appendChild(listBoxTooltip);

    const listBoxTooltipCreated = listboxOption
      .closest('[data-au-select]')
      .querySelector('[data-au-select-listbox-tooltip]');

    listboxOption.addEventListener('mousemove', (e) => {
      listBoxTooltipCreated.style.left = `${e.clientX - listBoxTooltipCreated.offsetWidth / 2}px`;
      listBoxTooltipCreated.style.top = `${e.clientY - 56}px`;

      listBoxTooltipCreated.style.visibility = 'visible';
      listBoxTooltipCreated.style.opacity = 1;
    });

    listboxOption.addEventListener('mouseleave', () => {
      listBoxTooltipCreated.style.visibility = 'hidden';
      listBoxTooltipCreated.style.opacity = 0;
    });
  }
}

function listboxRemoveTooltip(listboxOption) {
  if (listboxOption.closest('[data-au-select]').querySelector('[data-au-select-listbox-tooltip]')) {
    listboxOption.closest('[data-au-select]').querySelector('[data-au-select-listbox-tooltip]').remove();
  }
}

// function to force validation when we focusout a au-select element
function selectForceFocusAndBlur(el) {
  if (el.required) {
    el.focus();
    el.blur();
  }
}

function addEventListeners() {
  $$('[data-au-select]').forEach((select) => {
    const listbox = select.querySelector('[data-au-listbox]');
    const selectArrow = select.querySelector('[data-au-float-input-arrow]');
    const selectDisplayer = select.querySelector('[data-au-select-main]');
    const isSelectInline = select.dataset.auSelectInline === '';

    selectDisplayer.addEventListener('click', (e) => {
      e.preventDefault();
      displaySelect(select, listbox, selectArrow);
      listboxSelection(select, listbox, selectArrow);
    });

    selectDisplayer.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        displaySelect(select, listbox, selectArrow);
        listboxSelection(select, listbox, selectArrow);
      }
    });

    // to force validation
    selectDisplayer.addEventListener('blur', (e) => {
      const select = e.target.querySelector('select');
      const listBoxIshidden = e.target
        .closest('[data-au-select]')
        .querySelector('[data-au-listbox]')
        .classList.contains('au-visually-hidden');

      if (listBoxIshidden && select.value === '') {
        selectForceFocusAndBlur(select);
      }
    });

    if (isSelectInline) {
      listbox.style.left = `${selectDisplayer.offsetLeft}px`;
    }
  });
}

export default { init };
